import { useCallback, useEffect, useState } from 'react';
import { RENAME_ASSET_MAX_LENGTH } from '@frameio/assets/operations';
import { TextWithInlineEditing } from '@frameio/vapor';
import { useIsRenamingItem, useSetRenameItem } from '../../contexts';
import type { ProjectTreeItem } from '../../types';

export type EditableNameProps = {
  focused: boolean;
  isActive: boolean;
  item: ProjectTreeItem;
};

type Props = EditableNameProps & {
  ariaLabel: string;
  canUpdate: boolean;
  onRenameSubmit: (submitted: string) => void;
  originalName: string | undefined;
};

export function EditableName({
  ariaLabel,
  canUpdate,
  focused,
  isActive,
  item,
  onRenameSubmit,
  originalName,
}: Props) {
  const [controlledName, setControlledName] = useState(originalName);
  const setRenameItem = useSetRenameItem();
  const isInlineEditing = useIsRenamingItem(item);

  /**
   * This effect updates the controlledName in this component's state whenever
   * the folder name has been changed by an external source (usually via
   * an update to the cached folder data).
   */
  useEffect(() => {
    if (!isInlineEditing && originalName !== controlledName) {
      setControlledName(originalName);
    }
  }, [isInlineEditing, controlledName, originalName]);

  const handleRenameSubmit = useCallback(
    (submitted: string) => {
      const newName = submitted.trim();

      if (newName === originalName) return;

      onRenameSubmit(newName);
      setRenameItem(null);
    },
    [onRenameSubmit, originalName, setRenameItem]
  );

  const handleStoppedEditing = useCallback(
    (canceled = true) => {
      if (canceled || !controlledName || controlledName?.trim() === '') {
        setControlledName(originalName);
      }

      setRenameItem(null);
    },
    [controlledName, originalName, setRenameItem]
  );

  const handleEditingChange = useCallback(
    (editing: boolean) => {
      if (isInlineEditing && !editing) {
        handleStoppedEditing(false);
        return;
      }

      if (!isInlineEditing && editing) {
        setRenameItem(item);
      }
    },
    [handleStoppedEditing, isInlineEditing, item, setRenameItem]
  );

  const handleValueChange = useCallback((value: string) => {
    setControlledName(value);
  }, []);

  const enableInlineEdit =
    canUpdate && (isInlineEditing || focused || isActive);

  return (
    <TextWithInlineEditing
      aria-label={ariaLabel}
      autoTrim={true}
      color={isActive ? 'primary' : undefined}
      cursor={enableInlineEdit ? 'text' : 'pointer'}
      data-chromatic="ignore"
      data-testid={`project-tree--${controlledName}`}
      editing={canUpdate && isInlineEditing}
      enabled={enableInlineEdit}
      enableNewlines={false}
      enableTooltip={true}
      lineClamp={INLINE_EDIT_LINE_CLAMP}
      maxLength={RENAME_ASSET_MAX_LENGTH}
      maxRowsWhileEditing={INLINE_EDIT_MAX_ROWS}
      mode="button"
      onEditingCancelled={handleStoppedEditing}
      onEditingChange={handleEditingChange}
      onSubmit={handleRenameSubmit}
      onValueChange={handleValueChange}
      value={controlledName ?? undefined}
      variant="body"
    />
  );
}

const INLINE_EDIT_LINE_CLAMP = 1;
const INLINE_EDIT_MAX_ROWS = 1;
